import { compact, concat, first } from 'lodash';

import { BOTH_GRANTORS_SENTINEL } from '@/hooks/useDualGrantorOptions';
import { HouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  AugmentedCreateHypotheticalInsuranceInput,
  AugmentedUpdateHypotheticalInsuranceInput,
  HypotheticalInsuranceEndsOnKind,
  HypotheticalInsuranceTransferFrequency,
  HypotheticalInsuranceTransferTaxKind,
} from '@/types/schema';

import {
  InsurancePolicyDetails,
  InsurancePolicyDetailsSubformType,
} from '../../InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { mapInsurancePolicy } from '../../InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.utils';
import { HypotheticalIlitModalData_HypotheticalInsuranceFragment } from './graphql/HypotheticalILITModalData.generated';
import { HypotheticalILITModalContextShape } from './HypotheticalILITModal.context';
import {
  HypotheticalILITFormModalShape,
  HypotheticalILITInsurancePolicyType,
} from './HypotheticalILITModal.types';
import { getCreatedInsurancePolicyInputs } from './utils/createdInsurancePolicy';
import { getWithDestinationEntity } from './utils/destinationEntity';
import { getExistingInsuranceDetails } from './utils/existingInsurancePolicy';
import { getScalarFields } from './utils/scalarFields';
import { getSourceEntityDetails } from './utils/sourceEntity';
import {
  getCreateTransferTax,
  getUpdateTransferTax,
} from './utils/transferTax';

export function mapExistingHypotheticalILITToModal(
  data:
    | HypotheticalIlitModalData_HypotheticalInsuranceFragment
    | null
    | undefined
): HypotheticalILITFormModalShape | null {
  if (!data) return null;

  const policies: InsurancePolicyDetails[] = [];
  const initialPolicyIDs: string[] = [];
  let _hypotheticalInsurancePolicyType: HypotheticalILITInsurancePolicyType =
    HypotheticalILITInsurancePolicyType.Existing;

  if (data.existingInsurancePolicy) {
    policies.push(mapInsurancePolicy(data.existingInsurancePolicy));
    initialPolicyIDs.push(data.existingInsurancePolicy.id);
    _hypotheticalInsurancePolicyType =
      HypotheticalILITInsurancePolicyType.Existing;
  } else if (data.createdInsurancePolicy) {
    policies.push(mapInsurancePolicy(data.createdInsurancePolicy));
    initialPolicyIDs.push(data.createdInsurancePolicy.id);
    _hypotheticalInsurancePolicyType = HypotheticalILITInsurancePolicyType.New;
  }

  const insurancePoliciesSubform: InsurancePolicyDetailsSubformType = {
    policies,
    initialPolicyIDs,
    crummeyWithdrawalPeriodDays: null,
  };

  let transferTaxKind: string;

  if (
    data.transferTaxKind ===
    HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers
  ) {
    transferTaxKind =
      HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers;
  } else if (data.giftingGrantors && data.giftingGrantors.length > 1) {
    transferTaxKind = BOTH_GRANTORS_SENTINEL;
  } else if (data.giftingGrantors && data.giftingGrantors.length === 1) {
    const grantorId = first(data.giftingGrantors)?.id;
    if (grantorId) {
      transferTaxKind = grantorId;
    } else {
      // Default to AnnualExclusionGiftWithCrummeyPowers when grantor ID is null/empty
      transferTaxKind =
        HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers;
    }
  } else if (
    data.transferTaxKind === HypotheticalInsuranceTransferTaxKind.NonTaxable
  ) {
    transferTaxKind = HypotheticalInsuranceTransferTaxKind.NonTaxable;
  } else {
    // Default to AnnualExclusionGiftWithCrummeyPowers when giftingGrantors is null or empty
    transferTaxKind =
      HypotheticalInsuranceTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers;
  }

  const output: HypotheticalILITFormModalShape = {
    id: data.id,
    _createANewHypotheticalTrust: false,
    transferName: data.displayName,
    destinationEntityId: data.destinationEntity?.id ?? '',
    destinationEntityType: '',
    destinationEntityName: '',
    destinationEntityGrantor: '',
    existingInsurancePolicyId: data.existingInsurancePolicy?.id ?? '',
    createdInsurancePolicyId: data.createdInsurancePolicy?.id ?? null,
    startYear: data.startYear ?? undefined,
    endYear: data.endYear ?? undefined,
    endsOnKind:
      data.endsOnKind ?? HypotheticalInsuranceEndsOnKind.PolicyHolderDeath,
    transferFromId: data.sourceEntityID ?? data.sourceIndividualID ?? '',
    transferFrequency:
      data.transferFrequency ?? HypotheticalInsuranceTransferFrequency.OneTime,
    transferTaxKind,
    transferValue: data.transferValue ?? null,
    insurancePolicyDetailsSubform: insurancePoliciesSubform,
    _hypotheticalInsurancePolicyType,
  };

  return output;
}

export interface MapHypotheticalInsuranceToDataInput {
  formData: HypotheticalILITFormModalShape;
  contextData: HypotheticalILITModalContextShape;
  primaryClients: HouseholdDetailsContext['primaryClients'];
}

export function mapFormToCreateHypotheticalInsuranceInput(
  input: MapHypotheticalInsuranceToDataInput
): {
  create: AugmentedCreateHypotheticalInsuranceInput | null;
  errors: string[] | null;
} {
  const { householdId: householdID, waterfallId: estateWaterfallID } =
    input.contextData;

  const { scalars: scalarFields, errors: scalarFieldsErrors } =
    getScalarFields(input);

  const {
    error: existingInsurancePolicyError,
    clearExistingInsurancePolicy: _clearExistingInsurancePolicy, // we don't need this field
    ...existingInsurancePolicyDetails
  } = getExistingInsuranceDetails(input);

  const { error: sourceDetailsError, ...sourceDetails } =
    getSourceEntityDetails(input);

  const transferTax = getCreateTransferTax(input);

  const {
    withCreatedInsurancePolicy,
    clearCreatedInsurancePolicy: _clearCreatedInsurancePolicy, // we don't need this field
    errors: createdInsurancePolicyErrors,
  } = getCreatedInsurancePolicyInputs(input);

  const {
    withDestinationEntity,
    errors: destinationEntityErrors,
    destinationEntityID,
  } = getWithDestinationEntity(input);

  const errors: string[] = compact(
    concat(
      scalarFieldsErrors,
      existingInsurancePolicyError,
      sourceDetailsError,
      createdInsurancePolicyErrors,
      destinationEntityErrors
    )
  );

  if (errors.length || !scalarFields) {
    return {
      create: null,
      errors,
    };
  }

  const create: AugmentedCreateHypotheticalInsuranceInput = {
    create: {
      ...transferTax,
      ...scalarFields,
      ...existingInsurancePolicyDetails,
      ...sourceDetails,
      estateWaterfallID,
      destinationEntityID,
      householdID,
    },
    withCreatedInsurancePolicy,
    withDestinationEntity,
  };

  return {
    create,
    errors,
  };
}

export function mapFormToUpdateHypotheticalInsuranceInput(
  input: MapHypotheticalInsuranceToDataInput,
  id: string
): {
  update: AugmentedUpdateHypotheticalInsuranceInput | null;
  errors: string[] | null;
} {
  const {
    scalars: scalarFields,
    errors: scalarErrors,
    clearEndYear,
  } = getScalarFields(input);

  const { error: existingPolicyError, ...existingInsurancePolicyDetails } =
    getExistingInsuranceDetails(input);

  const { error: sourceError, ...sourceDetails } =
    getSourceEntityDetails(input);

  const {
    withCreatedInsurancePolicy,
    updateCreatedInsurancePolicy,
    clearCreatedInsurancePolicy,
    errors: createdInsurancePolicyErrors,
  } = getCreatedInsurancePolicyInputs(input);

  const {
    withDestinationEntity,
    errors: destinationEntityErrors,
    destinationEntityID,
    clearDestinationEntity,
  } = getWithDestinationEntity(input);

  const updateTransferTax = getUpdateTransferTax(input);

  const errors = compact(
    concat(
      scalarErrors,
      existingPolicyError,
      sourceError,
      createdInsurancePolicyErrors,
      destinationEntityErrors
    )
  );

  if (errors.length || !scalarFields) {
    return {
      update: null,
      errors,
    };
  }

  const update: AugmentedUpdateHypotheticalInsuranceInput = {
    id,
    update: {
      ...scalarFields,
      ...existingInsurancePolicyDetails,
      ...sourceDetails,
      ...updateTransferTax,
      destinationEntityID,
      clearDestinationEntity,
      clearEndYear,
      clearCreatedInsurancePolicy,
    },
    withCreatedInsurancePolicy,
    updateCreatedInsurancePolicy,
    withDestinationEntity,
  };

  return {
    update,
    errors: null,
  };
}
